import { useCallback, useMemo, useState } from 'react';

export type BulkSelectionScope = 'page' | 'filtered' | 'all';

export type UseBulkSelectionResult<T> = {
  selectedKeys: Set<string>;
  selectedCount: number;
  isSelected: (key: string) => boolean;
  toggleRow: (key: string) => void;
  togglePage: (pageKeys: string[]) => void;
  selectFiltered: (filteredKeys: string[]) => void;
  selectAll: (allKeys: string[]) => void;
  clearSelection: () => void;
  lastScope: BulkSelectionScope | null;
  pageAllSelected: (pageKeys: string[]) => boolean;
  pageSomeSelected: (pageKeys: string[]) => boolean;
  getSelectedRows: (rows: T[], getRowKey: (row: T) => string) => T[];
};

export function useBulkSelection<T>(): UseBulkSelectionResult<T> {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const [lastScope, setLastScope] = useState<BulkSelectionScope | null>(null);

  const isSelected = useCallback((key: string) => selectedKeys.has(key), [selectedKeys]);

  const toggleRow = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setLastScope(null);
  }, []);

  const togglePage = useCallback((pageKeys: string[]) => {
    setSelectedKeys((prev) => {
      const allOnPage = pageKeys.length > 0 && pageKeys.every((k) => prev.has(k));
      const next = new Set(prev);
      if (allOnPage) {
        pageKeys.forEach((k) => next.delete(k));
      } else {
        pageKeys.forEach((k) => next.add(k));
      }
      return next;
    });
    setLastScope('page');
  }, []);

  const selectFiltered = useCallback((filteredKeys: string[]) => {
    setSelectedKeys(new Set(filteredKeys));
    setLastScope('filtered');
  }, []);

  const selectAll = useCallback((allKeys: string[]) => {
    setSelectedKeys(new Set(allKeys));
    setLastScope('all');
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedKeys(new Set());
    setLastScope(null);
  }, []);

  const pageAllSelected = useCallback(
    (pageKeys: string[]) => pageKeys.length > 0 && pageKeys.every((k) => selectedKeys.has(k)),
    [selectedKeys],
  );

  const pageSomeSelected = useCallback(
    (pageKeys: string[]) => pageKeys.some((k) => selectedKeys.has(k)) && !pageAllSelected(pageKeys),
    [selectedKeys, pageAllSelected],
  );

  const getSelectedRows = useCallback(
    (rows: T[], getRowKey: (row: T) => string) => rows.filter((row) => selectedKeys.has(getRowKey(row))),
    [selectedKeys],
  );

  return useMemo(
    () => ({
      selectedKeys,
      selectedCount: selectedKeys.size,
      isSelected,
      toggleRow,
      togglePage,
      selectFiltered,
      selectAll,
      clearSelection,
      lastScope,
      pageAllSelected,
      pageSomeSelected,
      getSelectedRows,
    }),
    [
      selectedKeys,
      isSelected,
      toggleRow,
      togglePage,
      selectFiltered,
      selectAll,
      clearSelection,
      lastScope,
      pageAllSelected,
      pageSomeSelected,
      getSelectedRows,
    ],
  );
}
